import moment from "moment";

/** Enums */
import { NotificationEventLogStatuses, NotificationPriorities, NotificationReadStatuses, NotificationTypes } from "../../enums/Notification.ENUM";

const DEFAULT_NOTIFICATION_TITLE = "Notification";
const DEFAULT_NOTIFICATION_MESSAGE =
  "We couldn't load more details for this notification.";

const DEFAULT_TYPE_DISPLAY = Object.freeze({
  label: "Info",
  icon: "pi pi-bell",
  severity: "info",
  badgeType: "info",
});

const DEFAULT_PRIORITY_DISPLAY = Object.freeze({
  label: "Normal",
  icon: "pi pi-minus",
  severity: "info",
  badgeType: "info",
});

const DEFAULT_EVENT_STATUS_DISPLAY = Object.freeze({
  label: "Unknown",
  icon: "pi pi-minus-circle",
  severity: "neutral",
  badgeType: "gray",
});

export const NOTIFICATION_TYPE_DISPLAY = Object.freeze({
  [NotificationTypes.INFO]: {
    label: "Info",
    icon: "pi pi-info-circle",
    severity: "info",
    badgeType: "info",
  },
  [NotificationTypes.SUCCESS]: {
    label: "Success",
    icon: "pi pi-check-circle",
    severity: "success",
    badgeType: "success",
  },
  [NotificationTypes.WARNING]: {
    label: "Warning",
    icon: "pi pi-exclamation-triangle",
    severity: "warning",
    badgeType: "warning",
  },
  [NotificationTypes.ERROR]: {
    label: "Error",
    icon: "pi pi-times-circle",
    severity: "danger",
    badgeType: "danger",
  },
  [NotificationTypes.APPROVAL_REQUIRED]: {
    label: "Approval Required",
    icon: "pi pi-check-square",
    severity: "primary",
    badgeType: "primary",
  },
  [NotificationTypes.REMINDER]: {
    label: "Reminder",
    icon: "pi pi-clock",
    severity: "info",
    badgeType: "info",
  },
});

export const NOTIFICATION_PRIORITY_DISPLAY = Object.freeze({
  [NotificationPriorities.LOW]: {
    label: "Low",
    icon: "pi pi-angle-down",
    severity: "neutral",
    badgeType: "gray",
  },
  [NotificationPriorities.NORMAL]: {
    label: "Normal",
    icon: "pi pi-minus",
    severity: "info",
    badgeType: "info",
  },
  [NotificationPriorities.HIGH]: {
    label: "High",
    icon: "pi pi-arrow-up",
    severity: "warning",
    badgeType: "warning",
  },
  [NotificationPriorities.CRITICAL]: {
    label: "Critical",
    icon: "pi pi-exclamation-circle",
    severity: "danger",
    badgeType: "danger",
  },
});

export const NOTIFICATION_READ_STATUS_DISPLAY = Object.freeze({
  [NotificationReadStatuses.READ]: {
    label: "Read",
    icon: "pi pi-eye",
    severity: "neutral",
    badgeType: "gray",
  },
  [NotificationReadStatuses.UNREAD]: {
    label: "Unread",
    icon: "pi pi-circle-fill",
    severity: "primary",
    badgeType: "primary",
  },
});

export const NOTIFICATION_EVENT_STATUS_DISPLAY = Object.freeze({
  [NotificationEventLogStatuses.SUCCESS]: {
    label: "Success",
    icon: "pi pi-check-circle",
    severity: "success",
    badgeType: "success",
  },
  [NotificationEventLogStatuses.FAILED]: {
    label: "Failed",
    icon: "pi pi-times-circle",
    severity: "danger",
    badgeType: "danger",
  },
  [NotificationEventLogStatuses.SKIPPED]: {
    label: "Skipped",
    icon: "pi pi-step-forward",
    severity: "warning",
    badgeType: "warning",
  },
});

export const humanizeNotificationValue = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const normalizeNotificationDateTime = (dateValue) => {
  if (!dateValue || typeof dateValue !== "string") {
    return dateValue;
  }

  if (
    dateValue.endsWith("Z") ||
    dateValue.includes("+") ||
    /-\d{2}:\d{2}$/.test(dateValue)
  ) {
    return dateValue;
  }

  return `${dateValue}Z`;
};

export const formatNotificationDateTime = (
  dateValue,
  format = "DD MMM YYYY, hh:mm A",
  fallback = "",
) => {
  if (!dateValue) {
    return fallback;
  }

  const parsed = moment.utc(normalizeNotificationDateTime(dateValue)).local();
  return parsed.isValid() ? parsed.format(format) : fallback;
};

export const formatNotificationRelativeTime = (
  dateValue,
  fallback = "",
) => {
  if (!dateValue) {
    return fallback;
  }

  const parsed = moment.utc(normalizeNotificationDateTime(dateValue)).local();
  return parsed.isValid() ? parsed.fromNow() : fallback;
};

export const getNotificationTitle = (notification) =>
  notification?.title?.trim() || DEFAULT_NOTIFICATION_TITLE;

export const getNotificationMessage = (notification) =>
  notification?.message?.trim() || DEFAULT_NOTIFICATION_MESSAGE;

export const isNotificationUnread = (notification) =>
  !notification?.isRead && !notification?.readAt;

export const getNotificationReadStatusValue = (notification) =>
  isNotificationUnread(notification)
    ? NotificationReadStatuses.UNREAD
    : NotificationReadStatuses.READ;

export const getNotificationTypeDisplay = (type) => {
  if (!type) {
    return DEFAULT_TYPE_DISPLAY;
  }

  return (
    NOTIFICATION_TYPE_DISPLAY[type] ??
    NOTIFICATION_TYPE_DISPLAY[humanizeNotificationValue(type, type)] ??
    DEFAULT_TYPE_DISPLAY
  );
};

export const getNotificationPriorityDisplay = (priority) => {
  if (!priority) {
    return DEFAULT_PRIORITY_DISPLAY;
  }

  return (
    NOTIFICATION_PRIORITY_DISPLAY[priority] ??
    NOTIFICATION_PRIORITY_DISPLAY[humanizeNotificationValue(priority, priority)] ??
    DEFAULT_PRIORITY_DISPLAY
  );
};

export const getNotificationReadStatusDisplay = (notification) =>
  NOTIFICATION_READ_STATUS_DISPLAY[getNotificationReadStatusValue(notification)] ??
  NOTIFICATION_READ_STATUS_DISPLAY[NotificationReadStatuses.READ];

export const getNotificationEventStatusDisplay = (status) =>
  NOTIFICATION_EVENT_STATUS_DISPLAY[status] ?? DEFAULT_EVENT_STATUS_DISPLAY;

export const getNotificationReferenceLabel = (notification) => {
  const referenceType = humanizeNotificationValue(notification?.referenceType);
  const referenceId =
    notification?.referenceId !== null &&
      notification?.referenceId !== undefined &&
      notification?.referenceId !== ""
      ? `#${notification.referenceId}`
      : "";

  const label = [referenceType, referenceId].filter(Boolean).join(" ");

  return label;
};

export const normalizeNotificationItemsResponse = (
  response,
  fallbackPageSize = 10,
) => {
  const root = response?.data ?? response?.result ?? response ?? {};
  const items =
    (Array.isArray(root) && root) ||
    root?.items ||
    root?.notifications ||
    root?.results ||
    root?.rows ||
    root?.data ||
    [];

  return {
    items: Array.isArray(items) ? items : [],
    totalRecords:
      Number(
        root?.count ??
        root?.totalCount ??
        root?.totalRecords ??
        root?.total ??
        (Array.isArray(items) ? items.length : 0),
      ) || 0,
    pageNumber:
      Number(root?.pageNumber ?? root?.pageIndex ?? root?.currentPage ?? 1) || 1,
    pageSize:
      Number(root?.pageSize ?? root?.rows ?? fallbackPageSize) || fallbackPageSize,
    unreadCount: Number(root?.unreadCount ?? root?.unReadCount ?? 0) || 0,
  };
};

export const normalizeUnreadNotificationCount = (response) => {
  const root = response?.data ?? response ?? {};

  return (
    Number(
      root?.count ??
      root?.unreadCount ??
      root?.unReadCount ??
      root?.totalUnread ??
      root?.total,
    ) || 0
  );
};
